/** One day of the roster drawer's token chart: Classic and aOS side by side. */
export interface DailyTokens {
  date: string;
  classic: number;
  aos: number;
}

/**
 * Pure: the Classic daily series and the aOS daily series (customer users,
 * SEQUESTO staff left out) merged by day, oldest first. A day missing on one
 * side is 0 there. `aos` is null when the aOS ledger couldn't be read; the
 * aOS side is then 0 and the caller says so. Exported for tests.
 */
export function mergeDailyTokens(
  classic: ReadonlyArray<{ date: string; totalTokens: number }>,
  aos: ReadonlyArray<{ date: string; totalTokens: number }> | null,
): DailyTokens[] {
  const byDay = new Map<string, DailyTokens>();
  const day = (date: string) => {
    let d = byDay.get(date);
    if (!d) {
      d = { date, classic: 0, aos: 0 };
      byDay.set(date, d);
    }
    return d;
  };
  for (const p of classic) day(p.date).classic += p.totalTokens;
  for (const p of aos ?? []) day(p.date).aos += p.totalTokens;
  return [...byDay.values()].sort((a, b) => a.date.localeCompare(b.date));
}

/** Past this ratio between the two platforms' totals, the smaller line is flat on a shared axis. */
export const SPLIT_SCALE_RATIO = 5;

/**
 * Pure: whether the drawer draws Classic and aOS as two small charts, each on
 * its own axis, instead of one stacked chart. Only when both have tokens and
 * one dwarfs the other (GELITA-style aOS runs flatten Classic to the baseline).
 * Exported for tests.
 */
export function needsSplitScale(daily: ReadonlyArray<DailyTokens>): boolean {
  let classic = 0;
  let aos = 0;
  for (const d of daily) {
    classic += d.classic;
    aos += d.aos;
  }
  if (classic <= 0 || aos <= 0) return false;
  return Math.max(classic, aos) / Math.min(classic, aos) > SPLIT_SCALE_RATIO;
}
